import React, { useState, useEffect } from 'react'
import { useUnifiedAuth, useUser } from '../contexts'
import Img from './img'

interface UserAvatarProps {
  size?: number // Size in pixels, defaults to 40
  className?: string
  showName?: boolean
  nameClassName?: string
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  size = 40,
  className = '',
  showName = false,
  nameClassName = ''
}) => {
  const { authData } = useUnifiedAuth()
  const { profile } = useUser()
  const [mounted, setMounted] = useState(false)

  // Ensure component only renders client-side content after mounting
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional post-hydration mount flag; must flip after mount to avoid SSR/client hydration mismatch, so it cannot be derived during render
    setMounted(true)
  }, [])

  // Generate placeholder if no photo available
  // Use null until profile is loaded to avoid flash between placeholder-0 and correct one
  const lastDigit = profile?.id ? Number(profile.id) % 10 : null
  const alt = authData.displayName || 'User'
  const displayName = authData.displayName

  // Check if we have a local profile photo (not a social media URL)
  const hasLocalPhoto = mounted && authData.profilePhoto &&
    !authData.profilePhoto.includes('googleusercontent.com') &&
    !authData.profilePhoto.includes('facebook.com') &&
    !authData.profilePhoto.includes('fbcdn.net') &&
    !authData.profilePhoto.includes('platform-lookaside') &&
    !authData.profilePhoto.includes('default')

  // During SSR or before mounting, always render placeholder to avoid hydration mismatch
  // If lastDigit is null (profile not loaded), render empty div to avoid flash
  const sizeStyle = { width: size, height: size }

  const imageElement = hasLocalPhoto ? (
    <img
      src={authData.profilePhoto}
      alt={alt}
      style={sizeStyle}
      className={`rounded-full object-cover ${className}`}
      referrerPolicy="no-referrer"
    />
  ) : lastDigit !== null ? (
    <div style={sizeStyle} className={className}>
      <Img
        src={`/images/svg/profile-placeholder-${lastDigit}.svg`}
        alt={alt}
        className="w-full h-full"
      />
    </div>
  ) : (
    <div style={sizeStyle} className={className} />
  )

  if (showName) {
    return (
      <div className="flex items-center gap-3">
        {imageElement}
        <span className={nameClassName}>{displayName}</span>
      </div>
    )
  }

  return imageElement
}

export default UserAvatar
